/**
 * Utilitaires pour la gestion des nœuds du ChatFlowDiagram
 */

/**
 * Retourne l'icône correspondant au type de nœud
 */
export const getIcon = (type) => {
  switch (type?.toLowerCase()) {
    case 'queue': return '📋';
    case 'customer choice': return '🔀';
    case 'customerchoice': return '🔀';
    case 'form': return '📝';
    default: return '⚙️';
  }
};

/**
 * Retourne la couleur correspondant au type de nœud
 */
export const getColor = (type) => {
  switch (type?.toLowerCase()) {
    case 'queue': return '#3b82f6';
    case 'customer choice': return '#8b5cf6';
    case 'customerchoice': return '#8b5cf6';
    case 'form': return '#f59e0b';
    default: return '#6b7280';
  }
};

/**
 * Calcule la largeur totale d'un routing et ses enfants
 */
export const calculateRoutingWidth = (routingKey, routingItem, nodeWidth = 200) => {
  if (!routingItem) return nodeWidth;
  
  // Si c'est un Customer Choice avec plusieurs actions
  if ((routingItem.type?.toLowerCase() === 'customer choice' || routingItem.type?.toLowerCase() === 'customerchoice') 
      && routingItem.customerChoiceActions) {
    const actionCount = Object.keys(routingItem.customerChoiceActions).length;
    if (actionCount > 1) {
      // Largeur = parent + 250px par action supplémentaire
      const childrenWidth = (actionCount - 1) * 250;
      return nodeWidth + childrenWidth;
    }
  }
  
  return nodeWidth;
};

/**
 * Trouve le parent d'un routing
 */
export const findParent = (routingName, routingMap) => {
  for (const [key, r] of Object.entries(routingMap)) {
    if (r.type?.toLowerCase() === 'customer choice' && r.customerChoiceActions) {
      for (const action of Object.values(r.customerChoiceActions)) {
        if ((action.next?.type === 'routing' || action.next?.type === 'customerChoice' || action.next?.type === 'form') 
            && action.next.routingName === routingName) {
          return key;
        }
      }
    }
    if (r.type?.toLowerCase() === 'form' && r.next?.type === 'routing' && r.next.routingName === routingName) {
      return key;
    }
  }
  return null;
};

/**
 * Calcule le niveau d'un routing dans la hiérarchie
 */
export const calculateLevel = (routingName, routingMap, routingLevels, currentLevel = 0, visited = new Set()) => {
  if (visited.has(routingName)) return currentLevel;
  visited.add(routingName);
  
  // Garder le niveau maximum (le plus éloigné de la racine)
  if (!routingLevels.has(routingName)) {
    routingLevels.set(routingName, currentLevel);
  } else {
    const existingLevel = routingLevels.get(routingName);
    if (currentLevel > existingLevel) {
      routingLevels.set(routingName, currentLevel);
    }
  }
  
  const routing = routingMap[routingName];
  if (!routing) return currentLevel;
  
  // Parcourir les destinations
  if (routing.type?.toLowerCase() === 'customer choice' && routing.customerChoiceActions) {
    Object.values(routing.customerChoiceActions).forEach(action => {
      if (action.next?.type === 'routing' || action.next?.type === 'customerChoice' || action.next?.type === 'form') {
        calculateLevel(action.next.routingName, routingMap, routingLevels, currentLevel + 1, visited);
      }
    });
  }
  
  if (routing.type?.toLowerCase() === 'form' && routing.next?.type === 'routing') {
    calculateLevel(routing.next.routingName, routingMap, routingLevels, currentLevel + 1, visited);
  }
  
  return currentLevel;
};

/**
 * Parse l'ID d'un edge pour extraire les informations de connexion
 */
export const parseEdgeId = (edgeId) => {
  const parts = edgeId.split('-');
  const lastPart = parts[parts.length - 1];
  const hasActionKey = !isNaN(lastPart) && lastPart.length > 10; // ActionKey est un timestamp
  
  let sourceKey, targetKey, actionKey;
  
  if (hasActionKey) {
    // Format avec actionKey: e-routing-X-...-routing-Y-...-actionKey
    actionKey = lastPart;
    const firstRoutingIndex = 1;
    
    let sourceKeyParts = [];
    let targetStartIndex = -1;
    
    for (let i = firstRoutingIndex + 1; i < parts.length; i++) {
      if (parts[i] === 'routing' || parts[i] === 'queue') {
        targetStartIndex = i;
        break;
      }
      sourceKeyParts.push(parts[i]);
    }
    
    sourceKey = sourceKeyParts.join('_');
    
    let targetKeyParts = [];
    if (targetStartIndex > 0) {
      for (let i = targetStartIndex + 1; i < parts.length - 1; i++) {
        targetKeyParts.push(parts[i]);
      }
    }
    
    targetKey = targetKeyParts.join('_');
  } else {
    // Format sans actionKey
    const firstRoutingIndex = 1;
    let sourceKeyParts = [];
    let targetStartIndex = -1;
    
    for (let i = firstRoutingIndex + 1; i < parts.length; i++) {
      if (parts[i] === 'routing' || parts[i] === 'queue') {
        targetStartIndex = i;
        break;
      }
      sourceKeyParts.push(parts[i]);
    }
    
    sourceKey = sourceKeyParts.join('_');
    
    let targetKeyParts = [];
    if (targetStartIndex > 0) {
      for (let i = targetStartIndex + 1; i < parts.length; i++) {
        targetKeyParts.push(parts[i]);
      }
    }
    
    targetKey = targetKeyParts.join('_');
  }
  
  return { sourceKey, targetKey, actionKey, hasActionKey };
};
